import { useCallback, useRef, useState } from "react";
import { Upload, X, ImageIcon, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { fileToCompressedDataURL } from "../lib/imageUtils";
import { toast } from "sonner";

const MAX_IMAGES = 6;

export default function ProductImagesInput({ value = [], onChange }) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [processing, setProcessing] = useState(false);
    const images = Array.isArray(value) ? value : [];

    const handleFiles = useCallback(async (files) => {
        if (!files || files.length === 0) return;
        const room = MAX_IMAGES - images.length;
        if (room <= 0) {
            toast.error(`最多只能上传 ${MAX_IMAGES} 张图片`);
            return;
        }
        const arr = Array.from(files).slice(0, room);
        if (files.length > room) toast.info(`最多 ${MAX_IMAGES} 张，已省略多余的 ${files.length - room} 张`);
        setProcessing(true);
        try {
            const added = [];
            for (const f of arr) {
                if (!f.type.startsWith("image/")) continue;
                const url = await fileToCompressedDataURL(f);
                added.push(url);
            }
            if (added.length) onChange([...images, ...added].slice(0, MAX_IMAGES));
        } catch (e) {
            toast.error(e.message || "上传失败");
        } finally {
            setProcessing(false);
        }
    }, [images, onChange]);

    const remove = (idx) => onChange(images.filter((_, i) => i !== idx));
    const move = (from, to) => {
        if (to < 0 || to >= images.length) return;
        const next = [...images];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        onChange(next);
    };
    const setAsCover = (idx) => move(idx, 0);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer?.files);
    };

    return (
        <div className="space-y-3" data-testid="product-images-input">
            {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {images.map((url, idx) => (
                        <div
                            key={`${idx}-${url.slice(0, 24)}`}
                            data-testid={`product-image-thumb-${idx}`}
                            className="relative aspect-square bg-gold-50 border border-slate-200 rounded-md overflow-hidden group"
                        >
                            <img src={url} alt={`图片 ${idx + 1}`} className="w-full h-full object-cover" />

                            {/* First image marker */}
                            {idx === 0 && (
                                <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold-600 text-white text-[10px] font-bold shadow">
                                    <Star className="h-2.5 w-2.5 fill-current" /> 首图
                                </div>
                            )}

                            {/* Remove button */}
                            <button
                                type="button"
                                data-testid={`remove-image-${idx}`}
                                onClick={() => remove(idx)}
                                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                                title="删除"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>

                            {/* Reorder controls */}
                            <div className="absolute inset-x-1.5 bottom-1.5 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    type="button"
                                    data-testid={`move-image-left-${idx}`}
                                    onClick={() => move(idx, idx - 1)}
                                    disabled={idx === 0}
                                    className="h-7 w-7 rounded-full bg-white/95 text-slate-700 flex items-center justify-center shadow disabled:opacity-30 hover:bg-gold-100"
                                    title="向前"
                                >
                                    <ArrowLeft className="h-3.5 w-3.5" />
                                </button>
                                {idx !== 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setAsCover(idx)}
                                        className="text-[10px] font-semibold px-2 h-7 rounded-full bg-gold-600 text-white shadow hover:bg-gold-700"
                                        title="设为首图"
                                    >
                                        设首图
                                    </button>
                                )}
                                <button
                                    type="button"
                                    data-testid={`move-image-right-${idx}`}
                                    onClick={() => move(idx, idx + 1)}
                                    disabled={idx === images.length - 1}
                                    className="h-7 w-7 rounded-full bg-white/95 text-slate-700 flex items-center justify-center shadow disabled:opacity-30 hover:bg-gold-100"
                                    title="向后"
                                >
                                    <ArrowRight className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}

                    {images.length < MAX_IMAGES && (
                        <button
                            type="button"
                            data-testid="add-more-images-btn"
                            onClick={() => inputRef.current?.click()}
                            className="aspect-square rounded-md border-2 border-dashed border-slate-300 bg-gold-50 hover:border-gold-400 hover:bg-gold-50/60 flex flex-col items-center justify-center text-gold-600 transition-colors"
                        >
                            <Upload className="h-5 w-5 mb-1" />
                            <span className="text-xs font-medium">再加一张</span>
                        </button>
                    )}
                </div>
            )}

            {images.length === 0 && (
                <div
                    role="button"
                    tabIndex={0}
                    data-testid="product-images-dropzone"
                    onClick={() => inputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`relative flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed rounded-md cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-gold-400 ${
                        dragOver ? "border-gold-500 bg-gold-50" : "border-slate-300 bg-gold-50 hover:border-gold-400 hover:bg-gold-50/40"
                    }`}
                >
                    {processing ? (
                        <div className="text-sm text-gold-500">处理中...</div>
                    ) : (
                        <>
                            <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-2">
                                <Upload className="h-4 w-4 text-gold-500" />
                            </div>
                            <div className="text-sm font-medium text-slate-700">
                                拖放图片到这里，或<span className="text-gold-600">点击选择</span>
                            </div>
                            <div className="text-xs text-gold-500 mt-1 flex items-center gap-1">
                                <ImageIcon className="h-3 w-3" /> JPG / PNG · 最多 {MAX_IMAGES} 张 · 自动压缩至 900px
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between text-xs text-gold-600">
                <span>已上传 <span className="font-mono font-bold">{images.length}</span> / {MAX_IMAGES}</span>
                {images.length > 1 && <span className="text-gold-500">拖不动就点缩略图上的箭头调整顺序 · 首图会显示在商店列表页</span>}
            </div>

            <input
                ref={inputRef}
                data-testid="product-images-file-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { if (e.target.files) { handleFiles(e.target.files); e.target.value = ""; } }}
            />
        </div>
    );
}
